///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Sync-only layer for the historical earthquake catalog: pulls the
/// `earthquake_history` dataset (one static Arrow checkpoint, republished
/// a few times a year) into SQLite after everything the user can see has
/// loaded. It draws nothing; the cutout queries the table.
///

import { type Database } from "@/earthbrowser/database/Database"
import { type EarthBrowserContext } from "@/earthbrowser/EarthBrowserContext"
import { DatasetLayer, type ArrowRow, type DatasetFeature, type DatasetManifest, type DatasetSceneObject, type DatasetSyncConfig } from "../../DatasetLayer"
import { QuakeHistoryTable, type QuakeHistoryRow } from "./QuakeHistoryTable"

export class QuakeHistoryDatasetLayer extends DatasetLayer<DatasetFeature> {
	constructor(context: EarthBrowserContext) {
		super(context, "earthquake-history")
		this.visible = false
	}

	public getSyncConfig(): DatasetSyncConfig {
		return {
			datasetId: "earthquake_history",
			pollIntervalMs: 0,
			needsSync: true,
			supportsDelta: false,
			deferInitialSync: true,
		}
	}

	public async createTable(db: Database): Promise<void> {
		await QuakeHistoryTable.create(db)
	}

	public override async getStoredRowCount(_datasetId: string): Promise<number | null> {
		return QuakeHistoryTable.getCount()
	}

	public async applyCheckpoint(manifest: DatasetManifest, downloadArrowRows: (url: string) => Promise<ArrowRow[]>): Promise<void> {
		const shard = manifest.checkpoint.shards[0]
		if (!shard) return
		const rows = await downloadArrowRows(this.resolveUrl(shard.url, shard.path)) as Array<Record<string, unknown>>
		const parsed: QuakeHistoryRow[] = rows.map(r => ({
			id: String(r.id),
			time_ms: Number(r.time_ms),
			lat: Number(r.lat),
			lon: Number(r.lon),
			depth_km: Number(r.depth_km),
			mag: Number(r.mag),
			mag_type: typeof r.mag_type === "string" ? r.mag_type : "",
		}))
		await QuakeHistoryTable.replaceAll(parsed)
	}

	public createSceneObject(_feature: DatasetFeature): DatasetSceneObject<DatasetFeature> | undefined {
		return undefined
	}
}
